import sharedoSettings from "@/services/sharedo/sharedoSettingsService";
import queryString from "@/services/common/queryStringService";
import tokenStorage from "@/services/auth/tokenStorageService";
import global from "@/global";
import getPkce from 'oauth-pkce';

const normalize = (url) => {
  if (!url) return "";
  return url.endsWith("/") ? url : `${url}/`;
};

const redirectToSharedoLogin = () => {
  const settings = Object.assign({}, sharedoSettings.get());

  if(settings.enablePkce) {
    pkceLogin(settings);    
  } else {
    oldLogin(settings);
  } 
};

const oldLogin = (settings) => {
  const url = normalize(settings.identityServer)
    + "connect/authorize"
    + `?client_id=${settings.clientId}`
    + "&scope=sharedo offline_access"
    + "&response_type=code"
    + `&redirect_uri=${encodeURIComponent(settings.redirectUri)}`;

    window.location.href = url;
}

const pkceLogin = (settings) => {
    getPkce(64, (error, { verifier, challenge }) => {
        if (!error) {
            tokenStorage.setVerifierToken(verifier);

            const url = normalize(settings.identityServer)
            + "connect/authorize"
            + `?client_id=${settings.clientId}`
            + `&code_challenge=${challenge}`
            + "&code_challenge_method=S256"
            + "&scope=sharedo offline_access"
            + "&response_type=code"
            + `&redirect_uri=${encodeURIComponent(settings.redirectUri)}`;

            window.location.href = url;
        }
    });
}

const receiveCode = () => new Promise((resolve, reject) => {
    const queryParams = queryString.getParameters();
    if (!queryParams.code) {
        reject(queryParams.error || "Authentication error.");
    }

    const settings = Object.assign({}, sharedoSettings.get());
    const url = `${normalize(settings.identityServer)}connect/token`;
    
    
    let request = {
        method: "POST",
        cache: "no-cache",
        headers: {
            "Accept": "application/json",
            "Content-Type": "application/x-www-form-urlencoded"
        },
        body: "client_id=" + settings.clientId +
            "&client_secret=" + encodeURIComponent(settings.clientSecret) +
            "&grant_type=authorization_code" +
            "&scope=sharedo+offline_access" +
            "&redirect_uri=" + encodeURIComponent(settings.redirectUri) +
            "&code=" + queryParams.code
    };

    const verifier = tokenStorage.getVerifierToken();
    if (verifier) {
        request.body += `&code_verifier=${verifier}`
    }

    fetch(url, request).then(response => {
        if (!response.ok) {
            reject(`${response.status}: Could not exchange auth code.  Server responded with response of: ${response.statusText}`);
        } else {
            response.json().then(data => {
                const result = {
                    accessToken: data["access_token"],
                    refreshToken: data["refresh_token"],
                    state: queryParams.state
                };

                resolve(result);
            });
        }
    })
});

const renewTokens = () => new Promise((resolve, reject) => {
    const refreshToken = tokenStorage.getRefreshToken();
    if (!refreshToken) {
        reject("No refresh token available.");
    }

    const settings = Object.assign({}, sharedoSettings.get());
    const url = `${normalize(settings.identityServer)}connect/token`;
    const request = {
        method: "POST",
        cache: "no-cache",
        headers: {
            "Accept": "application/json",
            "Content-Type": "application/x-www-form-urlencoded"
        },
        body: "client_id=" + settings.clientId +
            "&client_secret=" + encodeURIComponent(settings.clientSecret) +
            "&grant_type=refresh_token" +
            "&scope=sharedo+offline_access" +
            "&refresh_token=" + refreshToken
    };

    fetch(url, request).then(response => {
        if (!response.ok) {
            reject(`${response.status}: Could not obtain new tokens.  Server responded with response of: ${response.statusText}`)
        } else {
            response.json().then(data => {
                const newAccessToken = data["access_token"];
                const newRefreshToken = data["refresh_token"];

                tokenStorage.setAccessToken(newAccessToken);
                tokenStorage.setRefreshToken(newRefreshToken);

                resolve(newAccessToken);
            }, error => reject(error));
        }
    });
});

const logout = async () => {
    const settings = Object.assign({}, sharedoSettings.get());
    const accessToken = tokenStorage.getAccessToken();
    const refreshToken = tokenStorage.getRefreshToken();

    if (accessToken) {
        await fetch(`${normalize(settings.identityServer)}connect/revocation`, {
            method: "POST",
            cache: "no-cache",
            headers: {
                "Content-Type": "application/x-www-form-urlencoded"
            },
            body: "client_id=" + settings.clientId +
                "&client_secret=" + encodeURIComponent(settings.clientSecret) +
                "&token=" + encodeURIComponent(accessToken) +
                "&token_type_hint=access_token"
        });
    }

    if (refreshToken) {
        await fetch(`${normalize(settings.identityServer)}connect/revocation`, {
            method: "POST",
            cache: "no-cache",
            headers: {
                "Content-Type": "application/x-www-form-urlencoded"
            },
            body: "client_id=" + settings.clientId +
                "&client_secret=" + encodeURIComponent(settings.clientSecret) +
                "&token=" + encodeURIComponent(refreshToken) +
                "&token_type_hint=refresh_token"
        });
    }

    tokenStorage.clear();
    global.getStore().commit("loggedOut");
}

const isAuthenticated = () => !!tokenStorage.getAccessToken();

export default {
    isAuthenticated,
    redirectToSharedoLogin,
    receiveCode,
    renewTokens,
    logout
};